/**
 * Corpus queries — §4.1.
 *
 * The corpus is a STATIC BUILD ARTEFACT: it cannot change while the tab is open, so a
 * refetch is pure waste and visited pages should stay resident. Hence
 * `staleTime: Infinity` and `gcTime: Infinity` on everything here.
 */
import { QueryClient, useQuery } from '@tanstack/react-query'
import {
  loadEnglishTafsirManifest,
  loadEnglishTafsirPage,
  loadMeta,
  loadPageWithFonts,
  loadReciterAudio,
  loadSearchIndex,
  loadTafsirPage,
  loadTranslationManifest,
  loadTranslationPage,
  loadTranslitManifest,
  loadTranslitPage,
  type LoadedPage,
  type Meta,
  type TranslationEdition,
  type TranslitEdition,
} from '@mushaf/corpus'

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      gcTime: Infinity,
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
})

export const metaQuery = {
  queryKey: ['meta'] as const,
  queryFn: ({ signal }: { signal?: AbortSignal }) => loadMeta(signal),
}

export const useMeta = () => useQuery(metaQuery)

/**
 * L1 — THE PAGE JSON AND ITS FONTS LOAD IN ONE QUERY FUNCTION, so page data is simply
 * never available early. This is the structural enforcement; nothing downstream has
 * to remember to wait.
 */
export function pageQuery(page: number, meta: Meta | undefined, withV4: boolean) {
  return {
    queryKey: ['page', page, withV4] as const,
    enabled: Boolean(meta),
    queryFn: ({ signal }: { signal?: AbortSignal }): Promise<LoadedPage> =>
      loadPageWithFonts(page, { meta: meta!, withV4, signal }),
  }
}

export function useMushafPage(page: number, withV4: boolean) {
  const { data: meta } = useMeta()
  return useQuery(pageQuery(page, meta, withV4))
}

export const useTafsirPage = (page: number, enabled = true) =>
  useQuery({
    queryKey: ['tafsir', page] as const,
    enabled,
    // §3.4/L20: tafsir is bundled PER MUSHAF PAGE (~23 KB), not per surah — per-surah
    // would mean a ~1 MB first click anywhere inside al-Baqarah.
    queryFn: ({ signal }) => loadTafsirPage(page, signal),
  })

/**
 * The English layer — §3.4/L20 again: one file per mushaf page, ~1.8 KB for a
 * translation and ~3.2 KB for the tafsir, so the panel's text arrives with the page
 * rather than on demand per ayah.
 */
export const useTranslationPage = (edition: TranslationEdition, page: number) =>
  useQuery({
    queryKey: ['translation', edition, page] as const,
    queryFn: ({ signal }) => loadTranslationPage(edition, page, signal),
  })

export const useEnglishTafsirPage = (page: number, enabled = true) =>
  useQuery({
    queryKey: ['english-tafsir', page] as const,
    enabled,
    queryFn: ({ signal }) => loadEnglishTafsirPage(page, signal),
  })

/**
 * The attribution the licence obliges us to display. Fetched once per edition and kept
 * for the session like everything else here — it is part of the same static artefact.
 */
export const useTranslationManifest = (edition: TranslationEdition) =>
  useQuery({
    queryKey: ['translation-manifest', edition] as const,
    queryFn: ({ signal }) => loadTranslationManifest(edition, signal),
  })

export const useEnglishTafsirManifest = (enabled = true) =>
  useQuery({
    queryKey: ['english-tafsir-manifest'] as const,
    enabled,
    queryFn: ({ signal }) => loadEnglishTafsirManifest(signal),
  })

/**
 * The transliteration sidecar. `enabled` so selecting `off` or the computed scheme costs
 * no request at all.
 */
export const useTranslitPage = (edition: TranslitEdition, page: number, enabled: boolean) =>
  useQuery({
    queryKey: ['translit', edition, page] as const,
    enabled,
    queryFn: ({ signal }) => loadTranslitPage(edition, page, signal),
  })

export const useTranslitManifest = (edition: TranslitEdition, enabled: boolean) =>
  useQuery({
    queryKey: ['translit-manifest', edition] as const,
    enabled,
    queryFn: ({ signal }) => loadTranslitManifest(edition, signal),
  })

export const useReciterAudio = (reciterId: string) =>
  useQuery({
    queryKey: ['audio', reciterId] as const,
    queryFn: ({ signal }) => loadReciterAudio(reciterId, signal),
  })

/** 2.8 MB, fetched when search first opens — NOT AT BOOT (§9). */
export const useSearchIndex = (enabled: boolean) =>
  useQuery({
    queryKey: ['search-index'] as const,
    enabled,
    queryFn: ({ signal }) => loadSearchIndex(signal),
  })
